import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  Search, RotateCw, Inbox, Activity, Container, Compass, Globe,
} from "lucide-react";
import {
  discoverServerServices,
  importDiscovered,
} from "../../redux/actionCreator";

/**
 * "Bring existing services in" wizard. Runs ONE ssh probe that gathers
 * `pm2 jlist`, `docker ps`, `kubectl get deploy`, and the nginx
 * sites-enabled listing, then renders them as a checklist. Picked rows
 * are POSTed to /discover/import which materialises them as deploy_apps
 * with mode + container_name + git/script/image pre-filled.
 *
 * Idempotent — already-imported items are flagged "already added" and
 * unselected by default. Admin can edit each app afterwards from the
 * Applications tab (target servers, env vars, port mappings, etc.).
 */
export default function DiscoverServicesModal({ show, onHide, projectId, server }) {
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    if (!projectId || !server?._id) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    setPicked(new Set());
    const res = await dispatch(discoverServerServices(projectId, server._id));
    setLoading(false);
    if (res?.status) setData(res.data);
    else {
      setData(null);
      setErr(res?.message || "Discover failed");
    }
  }, [dispatch, projectId, server?._id]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const existing = useMemo(
    () => new Set((data?.existing_container_names || []).map((s) => String(s).toLowerCase())),
    [data],
  );

  // Stable per-row key — kind + name + (container_id|namespace) — that we
  // use both for React rendering AND to gate the "already imported" badge.
  const keyOf = (it) =>
    `${it.kind}::${it.name || it.container_id || it.filename}::${it.namespace || it.container_id || ""}`;

  const isExisting = (it) => {
    const n = (it.name || it.container_id || it.filename || "").toLowerCase();
    return existing.has(n);
  };

  const toggle = (it) => {
    const k = keyOf(it);
    setPicked((p) => {
      const next = new Set(p);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const allRows = useMemo(
    () => [
      ...(data?.pm2 || []),
      ...(data?.docker || []),
      ...(data?.k8s || []),
      ...(data?.nginx || []),
    ],
    [data],
  );

  const pickAll = (kind) => {
    setPicked((p) => {
      const next = new Set(p);
      for (const it of allRows) {
        if (kind && it.kind !== kind) continue;
        if (isExisting(it)) continue;
        next.add(keyOf(it));
      }
      return next;
    });
  };
  const clearAll = () => setPicked(new Set());

  const doImport = async () => {
    const items = allRows.filter((it) => picked.has(keyOf(it)) && !isExisting(it));
    if (!items.length) return;
    setImporting(true);
    const res = await dispatch(importDiscovered(projectId, server._id, items));
    setImporting(false);
    setResult(res);
    if (res?.status) {
      // Re-probe so the "already imported" badges update.
      load();
    }
  };

  const Section = ({ title, badge, rows, columns }) => {
    if (!rows?.length) return null;
    const importableCount = rows.filter((r) => !isExisting(r)).length;
    return (
      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <strong>
            {title} <span className="badge bg-secondary ms-1">{rows.length}</span>
          </strong>
          <Button
            size="sm"
            variant="link"
            disabled={!importableCount}
            onClick={() => pickAll(badge)}
            className="p-0"
          >
            Select all ({importableCount})
          </Button>
        </div>
        <table className="emp-table mb-0">
          <thead>
            <tr>
              <th style={{ width: 32 }} />
              {columns.map((c) => <th key={c.key}>{c.label}</th>)}
              <th style={{ width: 1 }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((it) => {
              const already = isExisting(it);
              const k = keyOf(it);
              return (
                <tr key={k} className={already ? "text-muted" : ""}>
                  <td>
                    <input
                      type="checkbox"
                      checked={picked.has(k)}
                      disabled={already || importing}
                      onChange={() => toggle(it)}
                    />
                  </td>
                  {columns.map((c) => (
                    <td key={c.key}>
                      {c.render ? c.render(it) : it[c.key] ?? "—"}
                    </td>
                  ))}
                  <td className="text-end">
                    {already && (
                      <span className="badge bg-light text-muted" title="Already a deploy app">
                        already added
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const pickedCount = picked.size;
  const total =
    (data?.counts?.pm2 || 0) +
    (data?.counts?.docker || 0) +
    (data?.counts?.k8s || 0) +
    (data?.counts?.nginx || 0);

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <h5 className="m-0 d-inline-flex align-items-center gap-2">
          <Search size={16} /> Discover services — {server?.label || "server"}{" "}
          {server?.host && (
            <small className="text-muted">
              <code>{server.username}@{server.host}</code>
            </small>
          )}
        </h5>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div className="small text-muted">
            One SSH probe → pm2 / Docker / k8s / nginx. Pick the rows you want
            and they'll show up as <strong>Applications</strong> on the project,
            ready to deploy.
          </div>
          <Button size="sm" variant="light" onClick={load} disabled={loading || importing}>
            {loading ? <Spinner size="sm" animation="border" /> : <span className="d-inline-flex align-items-center gap-1"><RotateCw size={12} /> Re-scan</span>}
          </Button>
        </div>

        {err && <div className="alert alert-danger">{err}</div>}

        {loading && !data ? (
          <div className="text-center py-4">
            <Spinner animation="border" /> Probing the server…
          </div>
        ) : !data ? null : total === 0 ? (
          <div className="empty-state text-center py-4">
            <div className="empty-art"><Inbox size={32} strokeWidth={1.2} /></div>
            <p>
              Nothing detected on this server (no pm2 processes, Docker
              containers, k8s deployments, or nginx sites). Add applications
              manually from <strong>Deploy → Applications</strong> instead.
            </p>
          </div>
        ) : (
          <>
            <Section
              title={<span className="d-inline-flex align-items-center gap-2"><Activity size={14} /> PM2 processes</span>}
              badge="pm2"
              rows={data.pm2 || []}
              columns={[
                { key: "name", label: "Name" },
                { key: "status", label: "Status",
                  render: (it) => (
                    <span className={`badge bg-${it.status === "online" ? "success" : "secondary"}`}>
                      {it.status || "—"}
                    </span>
                  ),
                },
                { key: "instances", label: "Instances" },
                { key: "script", label: "Script",
                  render: (it) => <code className="small">{it.script || "—"}</code>,
                },
                { key: "cwd", label: "CWD",
                  render: (it) => <code className="small">{it.cwd || "—"}</code>,
                },
              ]}
            />
            <Section
              title={<span className="d-inline-flex align-items-center gap-2"><Container size={14} /> Docker containers</span>}
              badge="docker"
              rows={data.docker || []}
              columns={[
                { key: "name", label: "Name" },
                { key: "image", label: "Image",
                  render: (it) => <code className="small">{it.image}</code>,
                },
                { key: "ports", label: "Ports",
                  render: (it) => <code className="small">{it.ports || "—"}</code>,
                },
                { key: "status", label: "State" },
              ]}
            />
            <Section
              title={<span className="d-inline-flex align-items-center gap-2"><Compass size={14} /> Kubernetes deployments</span>}
              badge="k8s"
              rows={data.k8s || []}
              columns={[
                { key: "namespace", label: "Namespace" },
                { key: "name", label: "Name" },
                { key: "replicas", label: "Replicas",
                  render: (it) => `${it.ready ?? 0}/${it.replicas ?? 0}`,
                },
                { key: "images", label: "Images",
                  render: (it) => <code className="small">{(it.images || []).join(", ") || "—"}</code>,
                },
              ]}
            />
            <Section
              title={<span className="d-inline-flex align-items-center gap-2"><Globe size={14} /> nginx sites</span>}
              badge="nginx"
              rows={data.nginx || []}
              columns={[{ key: "filename", label: "Site file" }]}
            />
          </>
        )}

        {result && (
          <div className={`alert ${result.status ? "alert-success" : "alert-danger"} mt-3`}>
            <strong>{result.message}</strong>
            {result.data?.skipped?.length > 0 && (
              <ul className="small mb-0 mt-1">
                {result.data.skipped.map((s, i) => (
                  <li key={i}>
                    <code>{s.name}</code> — {s.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <Button size="sm" variant="link" onClick={clearAll} disabled={pickedCount === 0 || importing}>
          Clear selection
        </Button>
        <div className="d-flex gap-2">
          <Button variant="light" onClick={onHide} disabled={importing}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={doImport}
            disabled={pickedCount === 0 || importing}
          >
            {importing ? (
              <Spinner size="sm" animation="border" />
            ) : (
              `Import ${pickedCount} as application${pickedCount === 1 ? "" : "s"}`
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
